import { APP_INITIALIZER, FactoryProvider, ValueProvider } from '@angular/core';
import { SessionStorageService } from 'ngx-webstorage';
import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideHttpCache, withHttpCacheInterceptor } from '@ngneat/cashew';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { MatomoConfiguration, provideMatomo, withRouter } from 'ngx-matomo-client';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash-es';
import { SupportedLanguages } from './localization.service';
import { firstValueFrom } from 'rxjs';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';

export class CommonApplicationModuleProviders {
  public static getSessionIdAppInitializerProvider(): FactoryProvider {
    return {
      provide: APP_INITIALIZER,
      useFactory: CommonApplicationModuleProviders.getSessionIdProviderFactory(),
      deps: [SessionStorageService],
      multi: true,
    };
  }

  public static getAppNameValueProvider(value: string): ValueProvider {
    return {
      provide: 'appName',
      useValue: value,
    };
  }

  public static getTranslationsAppInitializer() {
    return {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService) => async () => {
        translate.addLangs(_.values(SupportedLanguages));
        translate.setDefaultLang('en');
        await firstValueFrom(translate.use('en'));
      },
      deps: [TranslateService],
      multi: true,
    };
  }

  public static getCheckUserAgentAppInitializerProvider(): FactoryProvider {
    return {
      provide: APP_INITIALIZER,
      // Needs to be indirect in this way to work
      // eslint-disable-next-line unicorn/consistent-function-scoping
      useFactory: () => () => {
        // User agent specific operations
        const ua = new UAParser();

        // set device type as html attribute in order to write sass mixins using this attribute
        document.documentElement.setAttribute('device-type', ua.getDevice().type ?? '');

        // Mobile Safari
        if (ua.getBrowser().name === 'Mobile Safari' || (ua.getOS().name === 'iOS' && ua.getBrowser().name === 'Safari')) {
          // Set viewport maximum scale=1.0 in order to disable auto zoom in input elements
          let viewport = document.querySelector('meta[name=viewport]');
          if (!viewport) {
            viewport = document.createElement('meta');
            viewport.setAttribute('name', 'viewport');
            document.querySelector('head')?.append(viewport);
          }
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0');
        }
      },
      deps: [],
      multi: true,
    };
  }

  static getHkMaterialFormFieldOptions(): ValueProvider {
    return {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', floatLabel: 'always', subscriptSizing: 'dynamic' },
    };
  }

  static getHkMaterialTabOptions(): ValueProvider {
    return {
      provide: MAT_TABS_CONFIG,
      useValue: { stretchTabs: false } as MatTabsConfig,
    };
  }

  static getHttpCache() {
    return provideHttpCache({
      strategy: 'explicit',
      ttl: 60 * 60 * 1000,
    });
  }

  static getMatomoProvider(config: MatomoConfiguration) {
    return provideMatomo(config, withRouter());
  }

  static getHttpClientWithDiAndCache() {
    return [
      provideHttpClient(
        withInterceptorsFromDi(),
        // The cache interceptor must be declared last to make sure the absolute paths are correct
        withInterceptors([withHttpCacheInterceptor()]),
      ),
    ];
  }

  static all(appName: string, config?: MatomoConfiguration) {
    const providers = [
      CommonApplicationModuleProviders.getHttpCache(),
      CommonApplicationModuleProviders.getHttpClientWithDiAndCache(),
      CommonApplicationModuleProviders.getAppNameValueProvider(appName),
      CommonApplicationModuleProviders.getSessionIdAppInitializerProvider(),
      CommonApplicationModuleProviders.getCheckUserAgentAppInitializerProvider(),
      CommonApplicationModuleProviders.getHkMaterialFormFieldOptions(),
      CommonApplicationModuleProviders.getHkMaterialTabOptions(),
      CommonApplicationModuleProviders.getTranslationsAppInitializer(),
    ];
    if (config) providers.push(CommonApplicationModuleProviders.getMatomoProvider(config));
    return providers;
  }

  private static getSessionIdProviderFactory(): (provider: SessionStorageService) => () => void {
    return (provider: SessionStorageService) => () => {
      const sessionId = provider.retrieve('sessionId');
      if (!sessionId) {
        provider.store('sessionId', uuidv4());
      }
    };
  }
}
